import React, { useState, useMemo } from "react";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import UseScreenSize from "../../../shared/useScreenSize";
import FilterCustomSelect from "./FilterCustomSelect";
import { useAppSelector } from "../../../redux/hooks";

interface FilterButtonProps {
  selectedLevel: "appLevel" | "roleLevel" | "featureLevel";
  filterValues: Array<{ title: string; value: string; id: number }>;
  setFilterValues: (
    values: Array<{ title: string; value: string; id: number }>,
  ) => void;
}

// Styles
const POPUP_STYLES = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    zIndex: 1200,
  },
  popup: {
    position: "absolute" as const,
    background: "#ffffff",
    border: "1px solid #d9d9d9",
    borderRadius: "8px",
    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.15)",
    padding: "16px",
    boxSizing: "border-box" as const,
  },
  header: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "22px",
    marginBottom: "12px",
  },
  description: {
    fontSize: "16px",
    lineHeight: "20px",
    color: "#58595b",
    marginBottom: "12px",
  },
  selectContainer: {
    display: "flex" as const,
    flexDirection: "column" as const,
    gap: "10px",
  },
  buttonContainer: {
    display: "flex" as const,
    justifyContent: "flex-end" as const,
    gap: "8px",
    marginTop: "16px",
  },
  buttonBase: {
    border: "1px solid",
    borderRadius: "4px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    height: "42px",
  },
  cancelButton: {
    borderColor: "#c8c8c8",
    background: "#ffffff",
  },
  applyButton: {
    borderColor: "#000000",
    background: "#000000",
    color: "#ffffff",
  },
};

const FilterButton: React.FC<FilterButtonProps> = ({
  selectedLevel,
  filterValues,
  setFilterValues,
}) => {
  const screenSize = UseScreenSize();
  const isResponsiveTablet = screenSize.width < 945;

  const appDomainState = useAppSelector(
    (state: any) => state.applicationDomain,
  );
  const appDomainData = appDomainState?.data ?? [];

  // NOTE: app_domain is fetched by the parent Dashboard with the correct env.
  // Do NOT fetch here — a second dispatch without env caused a duplicate
  // (no-env) app_domain call that raced with the Dashboard's env-scoped call.

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftAppIds, setDraftAppIds] = useState<number[]>([]);

  const open = Boolean(anchorEl);

  // Flatten all apps from all value streams for lookup
  const allApps = useMemo(
    () => appDomainData.flatMap((vs: any) => vs.applications ?? []),
    [appDomainData],
  );

  const filterOptions = useMemo(() => {
    if (selectedLevel === "appLevel") {
      return appDomainData.map((valueStream: any) => ({
        app_id: valueStream.value_stream_id,
        app_name: valueStream.value_stream_name,
      }));
    }
    return allApps;
  }, [selectedLevel, appDomainData, allApps]);

  const activeFilterCount = filterValues.length;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    // Restore previously applied selections
    const appliedIds = filterValues.map((f) => f.id);
    setDraftAppIds(appliedIds);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (draftAppIds.length === 0) {
      return;
    }
    const selectedFilters = draftAppIds
      .map((id) => filterOptions.find((option: any) => option.app_id === id))
      .filter(Boolean)
      .map((app: any) => ({
        title: selectedLevel === "appLevel" ? "VALUE STREAM" : "APP",
        value: app.app_name,
        id: app.app_id,
      }));
    setFilterValues(selectedFilters);
    handleCloseMenu();
  };

  const selectedOptionIds = draftAppIds;

  const filterValueStreams = useMemo(
    () => [
      {
        value_stream_id: 0,
        value_stream_name: "Filter Options",
        applications: filterOptions,
      },
    ],
    [filterOptions],
  );

  const popupWidth = isResponsiveTablet ? "240px" : "320px";
  const popupLeft = anchorEl
    ? Math.max(
        16,
        anchorEl.getBoundingClientRect().right -
          (isResponsiveTablet ? 240 : 320),
      )
    : 0;
  const popupTop = anchorEl ? anchorEl.getBoundingClientRect().bottom + 8 : 0;

  const isApplyDisabled = draftAppIds.length === 0;

  return (
    <>
      <button
        type="button"
        className="button-transparent"
        onClick={handleOpenMenu}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "40px",
          border: "1px solid #000000",
          borderRadius: "4px",
          padding: "0 10px",
          gap: "8px",
          backgroundColor: "#ffffff",
          cursor: "pointer",
        }}
      >
        {activeFilterCount > 0 && (
          <span
            style={{
              minWidth: "18px",
              height: "18px",
              padding: "0 4px",
              borderRadius: "2px",
              backgroundColor: "#000000",
              color: "#ffffff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: "12px",
            }}
          >
            {activeFilterCount}
          </span>
        )}
        <span
          style={{
            fontSize: "14px",
            lineHeight: "16px",
            fontWeight: 500,
            color: "#000000",
          }}
        >
          Filter
        </span>
        <FilterListOutlinedIcon style={{ height: "16px", width: "16px" }} />
      </button>

      {open && anchorEl && (
        <div
          style={POPUP_STYLES.overlay}
          onClick={handleCloseMenu}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Escape" && handleCloseMenu()}
        >
          <div
            style={{
              ...POPUP_STYLES.popup,
              top: popupTop,
              left: popupLeft,
              width: popupWidth,
            }}
            role="button"
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={POPUP_STYLES.header}>Apply Filter</div>
            <div style={POPUP_STYLES.description}>
              Select one or more options.
            </div>

            <div style={POPUP_STYLES.selectContainer}>
              <FilterCustomSelect
                valueStreams={filterValueStreams}
                selectedOptions={{
                  app_id: selectedOptionIds,
                }}
                onChange={(selectedValues: { app_id: number[] }) => {
                  setDraftAppIds(selectedValues.app_id);
                }}
                placeholder={
                  selectedLevel === "appLevel"
                    ? "Select Value Stream"
                    : "Select Applications"
                }
                optionsSx={{
                  width: "100%",
                  minWidth: "100%",
                  "& .MuiSelect-select": {
                    minHeight: "20px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              />
            </div>

            <div style={POPUP_STYLES.buttonContainer}>
              <button
                type="button"
                onClick={handleCloseMenu}
                style={{
                  ...POPUP_STYLES.buttonBase,
                  ...POPUP_STYLES.cancelButton,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={isApplyDisabled}
                style={{
                  ...POPUP_STYLES.buttonBase,
                  ...POPUP_STYLES.applyButton,
                  opacity: isApplyDisabled ? 0.6 : 1,
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterButton;
